<template>
  <v-card v-if="mitigationGroup" id="mitigation-group-form">
    <v-card-title class="primary white--text">
      <v-row class="pa-2" justify="space-between">
        <span class="headline">
          {{ title }}
        </span>
        <BaseDialogActions hideRefresh hideFullscreen />
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-text-field
      class="pt-4"
      v-model="mitigationGroup.name"
      :rules="[rules.required]"
      :label="$t('groupName')"
      :placeholder="$t('groupNamePlaceholder')"
      color="black"
      :counter="maxNameLength"
      :maxlength="maxNameLength"
      ref="mitigationGroupName"/>
      <v-textarea
      class="pt-2"
      v-model="mitigationGroup.description"
      :label="$t('description')"
      :placeholder="$t('groupDescriptionPlaceholder')"
      color="black"
      rows="3"
      auto-grow
      :counter="maxDescriptionLength"
      :maxlength="maxDescriptionLength"/>
      <p class="text-subtitle-2 mb-1 mt-2">{{ $t('color') }}</p>
      <p class="text-caption grey--text text--darken-1 mb-1">{{ $t('groupColorMapHint') }}</p>
      <p class="text-caption grey--text text--darken-1 mb-1">{{ $t('groupColorUniqueHint') }}</p>
      <div ref="colorPickerContainer" style="width: 100%;">
        <v-color-picker
        v-if="pickerWidth > 0"
        :key="pickerWidth"
        v-model="mitigationGroup.color"
        mode="hexa"
        hide-mode-switch
        flat
        :width="pickerWidth"/>
        <div class="d-flex flex-wrap mt-2">
          <v-tooltip
          v-for="swatch in swatchOptions"
          :key="swatch.color"
          bottom
          color="grey"
          :disabled="!swatch.takenBy">
            <template #activator="{ on }">
              <span v-on="on">
                <span
                class="mg-swatch-option mr-2"
                :class="{ 'mg-swatch-taken': !!swatch.takenBy, 'mg-swatch-selected': swatch.selected }"
                :style="{ backgroundColor: swatch.color }"
                @click="swatchClicked(swatch)"/>
              </span>
            </template>
            <span class="subtitle-1 white--text">{{ $t('colorInUseBy', { group: swatch.takenBy }) }}</span>
          </v-tooltip>
        </div>
        <p v-if="colorTakenBy" class="text-caption error--text mb-0 mt-1">
          {{ $t('colorInUseBy', { group: colorTakenBy }) }}
        </p>
      </div>
      <v-checkbox
      v-model="mitigationGroup.requiresHabitat"
      :label="$t('requireHabitatSelection')"
      :hint="$t('requireHabitatSelectionInfo')"
      persistent-hint
      color="primary"/>
      <v-textarea
      v-if="!mitigationGroup.requiresHabitat"
      class="pt-4"
      v-model="mitigationGroup.mitigationSteps"
      :label="$t('mitigationStepsOptional')"
      :placeholder="$t('groupStepsPlaceholder')"
      :hint="$t('groupStepsInfo')"
      persistent-hint
      color="black"
      rows="4"
      auto-grow
      :counter="maxStepsLength"
      :maxlength="maxStepsLength"/>
      <v-row no-gutters justify="end" class="mt-4">
        <v-btn id="mitigation-group-submit" color="primary" @click="submitClicked" :disabled="!canSubmit">
          {{ buttonText }}
        </v-btn>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import fieldRules from '@/utils/rules.js'
import { StringConstants } from '@/utils/constants'
import { cloneDeep } from 'lodash'

const GROUP_COLOR_SWATCHES = ['#D64541', '#E0982C', '#43A047', '#5062CC', '#795548', '#9E9E9E']

const DEFAULT_GROUP_COLOR = '#D64541'

const HEX_COLOR_PATTERN = /^#[0-9A-F]{6}$/

const toHex = (color) => (color || DEFAULT_GROUP_COLOR).substring(0, 7).toUpperCase()

export default {
  name: 'MitigationGroupForm',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    propMitigationGroup: {
      type: Object,
      required: false,
      default: undefined
    }
  },

  created () {
    if (this.propMitigationGroup) {
      this.mitigationGroup = cloneDeep(this.propMitigationGroup)
      this.mitigationGroup.description = this.mitigationGroup.description ?? ''
      this.mitigationGroup.mitigationSteps = this.mitigationGroup.mitigationSteps ?? ''
      this.mitigationGroup.color = toHex(this.mitigationGroup.color)
      this.mitigationGroup.requiresHabitat = !!this.mitigationGroup.requiresHabitat
    } else {
      this.mitigationGroup.color = this.colorSwatches.find(color => !this.takenColors[color]) ?? DEFAULT_GROUP_COLOR
    }
  },

  mounted () {
    setTimeout(_ => {
      this.$refs.mitigationGroupName.focus()
      this.measurePickerWidth()
    }, 0)
    window.addEventListener('resize', this.measurePickerWidth)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.measurePickerWidth)
  },

  computed: {
    ...mapGetters('mitigation-group', ['allMitigationGroups']),

    editing () {
      return this.mitigationGroup?.mitigationGroupId
    },

    title () {
      return this.editing ? this.$t('editMitigationGroup') : this.$t('addMitigationGroup')
    },

    buttonText () {
      return this.editing ? this.$t('saveChanges') : this.$t('create')
    },

    takenColors () {
      return this.allMitigationGroups
        .filter(group => group.mitigationGroupId !== this.mitigationGroup.mitigationGroupId && !!group.color)
        .reduce((taken, group) => ({ ...taken, [group.color.toUpperCase()]: group.name }), {})
    },

    swatchOptions () {
      const selectedColor = toHex(this.mitigationGroup.color)
      return this.colorSwatches.map(color => ({
        color,
        takenBy: this.takenColors[color],
        selected: color === selectedColor
      }))
    },

    colorTakenBy () {
      return this.takenColors[toHex(this.mitigationGroup.color)]
    },

    colorIsValid () {
      return HEX_COLOR_PATTERN.test(toHex(this.mitigationGroup.color))
    },

    canSubmit () {
      const name = this.mitigationGroup.name.trim()
      return name.length > 0 && name.length <= this.maxNameLength && this.colorIsValid && !this.colorTakenBy
    }
  },

  data: () => ({
    mitigationGroup: {
      name: '',
      description: '',
      color: DEFAULT_GROUP_COLOR,
      requiresHabitat: true,
      mitigationSteps: ''
    },
    pickerWidth: 0,
    maxNameLength: StringConstants.MediumStringLength,
    maxDescriptionLength: StringConstants.ExtraExtraLongStringLength,
    maxStepsLength: StringConstants.ExtraExtraLongStringLength,
    colorSwatches: GROUP_COLOR_SWATCHES,
    rules: fieldRules.rules
  }),

  methods: {
    ...mapActions('mitigation-group', ['createMitigationGroup', 'updateMitigationGroup']),

    measurePickerWidth () {
      const width = this.$refs.colorPickerContainer?.clientWidth
      if (width) this.pickerWidth = width
    },

    swatchClicked (swatch) {
      if (swatch.takenBy) return
      this.mitigationGroup.color = swatch.color
    },

    async submitClicked () {
      if (this.colorTakenBy) {
        this.setSnackError(this.$t('colorInUseBy', { group: this.colorTakenBy }))
        return
      }

      if (!this.colorIsValid) {
        this.setSnackError(this.$t('colorIsRequired'))
        return
      }

      if (!this.canSubmit) {
        this.setSnackError(this.$t('nameIsRequired'))
        return
      }

      const requestObj = cloneDeep(this.mitigationGroup)
      requestObj.name = requestObj.name.trim()
      requestObj.description = (requestObj.description ?? '').trim()
      requestObj.color = toHex(requestObj.color)
      requestObj.mitigationSteps = requestObj.requiresHabitat ? '' : (requestObj.mitigationSteps ?? '').trim()

      if (this.editing) {
        await this.updateMitigationGroup(requestObj)
        this.setSnack(this.$t('mitigationGroupUpdated'))
      } else {
        await this.createMitigationGroup(requestObj)
        this.setSnack(this.$t('mitigationGroupCreated'))
      }
      this.$emit('mitigation-group-mutated')
    }
  }
}
</script>

<style scoped>
.mg-swatch-option {
  width: 1.75em;
  height: 1.75em;
  border-radius: 0.2em;
  display: inline-block;
  cursor: pointer;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, .12) inset;
}

.mg-swatch-selected {
  box-shadow: 0 0 0 2px rgba(0, 0, 0, .87);
}

.mg-swatch-taken {
  cursor: not-allowed;
  opacity: 0.25;
}
</style>
